// Model
import {
    GetQueuesFilters,
    QueueModel,
    QueueOptionSets,
    QueueRolesResponseModel,
    QueuesResponseModel,
    ResolveByDateAndLimitPayload,
    RolesModel,
    SaveQueueEditorPayload,
} from '../model/queue.model';
import { HttpClient, HttpParams } from '@angular/common/http';
// Services
import { CLASS_VALUATION_PROVIDER, SystemApiService } from '@cv-invision/core';
// Angular
import { Injectable } from '@angular/core';
import { catchError, forkJoin, map, Observable, of, Subject, switchMap } from 'rxjs';
import { PaginationModel } from '@cv-invision/cv.component-library.ux';
import { TrackingModel } from '../model/tracking.model';

@Injectable({ providedIn: 'root' })
export class QueuesService {
    private trackings: TrackingModel[] = [
        {
            name: 'Appscope',
            id: 'appscope',
        },
        {
            name: 'CMP Acuity',
            id: 'cmpacuity',
        },
    ];
    // private subject that triggers reloads
    private refreshQueuesSubject = new Subject<void>();

    // public observable for components to subscribe to
    public $refreshQueues = this.refreshQueuesSubject.asObservable();
    private sourceMap: Record<string, (item: QueueOptionSets) => Observable<string[]>> = {
        // For 'xxx', load product IDs from your existing product call
        xxx: () => this.getProductIds(),
    };
    constructor(private httpClient: HttpClient, private systemApiService: SystemApiService) {}

    /**
     * Get the existing queues.
     * @returns
     */
    getQueues(
        pagination: PaginationModel,
        queuesFilters?: GetQueuesFilters,
    ): Observable<QueuesResponseModel> {
        let params = new HttpParams();
        if (queuesFilters?.name) {
            params = params.append('Name', queuesFilters.name);
        }
        if (pagination) {
            params = params.append('limit', pagination.limit.toString());
            params = params.append('offset', pagination.offset.toString());
        }
        return this.httpClient
            .get<QueuesResponseModel>(
                `${this.systemApiService.api['intelligenceService']}manager/queues/search`,
                { params },
            )
            .pipe(
                map((payload: any) => {
                    const queueResponse: QueuesResponseModel = {
                        data: payload.data ?? [],
                        totalCount: payload.totalCount ?? 0,
                        offset: payload.offset ?? 0,
                        limit: payload.limit ?? 0,
                    };
                    return queueResponse;
                }),
            );
    }

    /**
     * Resolve the existing queues by reference id/external id.
     * @returns {Observable<any>}
     */
    resolveQueuesByExternalId(externalId: string): Observable<any> {
        return this.httpClient.post<QueuesResponseModel>(
            `${this.systemApiService.api['intelligenceService']}manager/queues/resolves/externalId/${externalId}`, {}
        );
    }

    /**
     * Get the existing queues.
     * @returns
     */
    resolveQueuesByLimitAndDate(payload: ResolveByDateAndLimitPayload): Observable<any> {
      return this.httpClient.post<QueuesResponseModel>(
            `${this.systemApiService.api['intelligenceService']}manager/queues/resolves/dates`,
            {...payload}
        );
    }

    /**
     * Refresh Queues
     */
    refreshQueues() {
        this.refreshQueuesSubject.next();
    }

    /**
     * Obtains a ruleset products collection by tracking id.
     * @param {string[]} trackingIds - The tracking id passed to the endpoint.
     *
     * @returns {Observable<ProductModel[]>} - An observable containing a collection of products.
     */
    getProductIds(productTitleFilter?: string): Observable<string[]> {
        const limit = 50;
        const requests = this.trackings.map((tracking) => {
            let params = new HttpParams().set('Limit', limit);
            if (productTitleFilter && productTitleFilter.trim().length > 0) {
                params = params.set('Title', productTitleFilter.trim());
            }
            // Append tracking at the end of the URL
            return this.httpClient
                .get<any>(`${this.systemApiService.api['orders']}/products/${tracking.id}`, {
                    params,
                })
                .pipe(
                    map((response) => {
                        return (
                            response.map((product) => {
                                return product.id;
                            }) ?? []
                        );
                    }),
                    catchError((err) => {
                        console.error(`Failed for tracking ${tracking}:`, err);
                        return of([]);
                    }),
                );
        });

        return forkJoin(requests).pipe(map((results: string[]) => results.flat()));
    }

    /**
     * GET - Returns list of roles.
     */
    getQueueRoles(): Observable<QueueRolesResponseModel> {
        return this.httpClient
            .get(
                `${this.systemApiService.api['identitySystemService']}${CLASS_VALUATION_PROVIDER}/roles`,
            )
            .pipe(
                map((response: any) => {
                    const rolesResponse: QueueRolesResponseModel = {
                        data:
                            response?.data.map((role: RolesModel) => {
                                const roleModel: RolesModel = {
                                    name: `${role.name} - (${role.id})`,
                                    id: role.id ?? '',
                                };
                                return roleModel;
                            }) ?? [],
                        totalCount: response?.totalCount ?? 0,
                        offset: response?.offset ?? 0,
                        limit: response?.limit ?? 0,
                    };
                    return rolesResponse;
                }),
            );
    }

    /**
     * Retrieves all the options sets available for the queue building process
     * @returns {Observable<QueueOptionSets[]>}
     */
    getQueueEditorOptionSets(): Observable<QueueOptionSets[]> {
        return this.httpClient
            .get<QueueOptionSets[]>(
                `${this.systemApiService.api['intelligenceService']}manager/queues/fields`,
            )
            .pipe(
                switchMap((items: QueueOptionSets[] = []) => {
                    if (!items.length) return of([]);

                    const resolvers = items.map((item) => {
                        // no source -> return as-is
                        if (!item.source) return of(item);
                        // known source? load options and merge
                        const loader = this.sourceMap[item.source.toLowerCase()];
                        if (!loader) return of(item); // unknown source, keep original
                        return loader(item).pipe(
                            map((opts) => ({ ...item, options: opts } as QueueOptionSets)),
                            catchError((err) => {
                                console.error(
                                    `Failed resolving source '${item.source}' for key '${item.key}'`,
                                    err,
                                );
                                return of(item); // keep item without options on failure
                            }),
                        );
                    });

                    // Wait for all items to be resolved
                    return forkJoin(resolvers);
                }),
            );
    }

    /**
     * Save Queue
     */
    saveQueueEditor(payload: SaveQueueEditorPayload) {
        return this.httpClient.post<QueueOptionSets[]>(
            `${this.systemApiService.api['intelligenceService']}manager/queues`,
            { ...payload },
        );
    }

    /**
     * Save Queue
     */
    editQueue(payload: SaveQueueEditorPayload, queueId: number) {
        return this.httpClient.put<QueueOptionSets[]>(
            `${this.systemApiService.api['intelligenceService']}manager/queues/${queueId}`,
            { ...payload },
        );
    }

    /**
     * Delete a queue by queue id
     * @param queueId
     * @returns
     */
    deleteQueue(queueId: number) {
        return this.httpClient.delete<QueueOptionSets[]>(
            `${this.systemApiService.api['intelligenceService']}manager/queues/${queueId}`,
        );
    }
}
